import React, {forwardRef, useImperativeHandle, useState} from "react";
import {Box, Chip, CircularProgress, Divider, IconButton, TextField, Typography} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import {FocusFormHandle, FormValidationHandle} from "./WordInfoEditView";

export interface WordAndSynonymEditViewProps {
    title: string
    values: string[]
    onChange: (newValues: string[]) => void
    spellCheck: boolean
    autoFocus: boolean
    isLoading: boolean
    onGenerationButtonClick?: () => void
    generationButtonLoading?: boolean
    additionalDisplaySlot?: React.ReactNode
    allowSynonyms?: boolean
}

export const WordAndSynonymEditView = forwardRef<FormValidationHandle & Partial<FocusFormHandle>, WordAndSynonymEditViewProps>(({title, values, onChange, spellCheck, autoFocus, isLoading, onGenerationButtonClick, generationButtonLoading, additionalDisplaySlot, allowSynonyms = true}, ref) =>  {

    const [isAddingSynonym, setIsAddingSynonym] = useState<boolean>(false);
    const [currentSynonymInput, setCurrentSynonymInput] = useState<string>('');
    const [validationError, setValidationError] = useState<string | null>(null);

    const mainValueInputRef = React.useRef<HTMLInputElement>(null);
    const synonymInputRef = React.useRef<HTMLInputElement>(null);


    useImperativeHandle(ref, () => ({
        validateForm,
        focusInput
    }));


    const addSynonym = (keepSynonymEditMode = true) => {
        if (currentSynonymInput.trim().length > 0) {
            onChange([...values, currentSynonymInput]);
        }
        setCurrentSynonymInput('');
        if (keepSynonymEditMode)
            synonymInputRef.current?.focus();
        else
            setIsAddingSynonym(false);
    }

    const cancelSynonymAddition = () => {
        setCurrentSynonymInput('');
        setIsAddingSynonym(false);
    }

    const validateForm = (): boolean => {
        const isValid = !!values[0] && values[0].trim().length > 0;
        setValidationError(isValid ? null : "Field required");
        return isValid;
    }

    const focusInput = () => {
        mainValueInputRef.current!.focus();
    }


    return (
        <Box mb={2}>
            <Divider textAlign="left">
                <Typography variant="h6" align="center">{title}</Typography>
            </Divider>
            <Box sx={{mb: 1}}/>
            <Box display="flex" alignItems="start" gap={1}>
                <TextField
                    variant="outlined"
                    spellCheck={spellCheck}
                    autoFocus={autoFocus}
                    autoCapitalize="off"
                    autoComplete="off"
                    fullWidth={true}
                    color="secondary"
                    value={values[0] || ''}
                    size="small"
                    disabled={isLoading}
                    error={!!validationError}
                    helperText={validationError}
                    onChange={(e) => {
                        if (validationError && e.target.value.trim().length > 0)
                            setValidationError(null);
                        onChange([e.target.value, ...values.slice(1)]);
                    }}
                    onKeyDown={(e) => {
                        if (allowSynonyms && e.key === 'Enter' && values[0])
                            setIsAddingSynonym(true);
                    }}
                    InputProps={{
                        style: { fontSize: 16 },
                        startAdornment: isLoading ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                                <CircularProgress size={20} color="secondary" />
                            </Box>
                        ) : null,
                        endAdornment:
                            !allowSynonyms || isAddingSynonym || !values[0] ? (
                                <></>
                            ) : (
                                <IconButton title={"Add synonyms"} color="secondary" onClick={() => setIsAddingSynonym(true)}>
                                    <AddIcon />
                                </IconButton>
                            ),
                    }}
                    inputRef={mainValueInputRef}
                    sx={{ flexGrow: 1 }} // takes remaining space
                />
                {onGenerationButtonClick && (generationButtonLoading ? (
                        <CircularProgress size={24} color="primary" />
                    ) : (
                    <IconButton
                        color="primary"
                        title={"Populate all fields"}
                        onClick={onGenerationButtonClick}
                        disabled={!values[0]}
                        sx={{ flexShrink: 0, backgroundColor: "primary.main", color: "black", "&:hover": { backgroundColor: "primary.dark" } }} // prevents shrinking
                    >
                        <AutoAwesomeIcon />
                    </IconButton>
                    ))}
            </Box>
            <Box sx={{mb: 1}}/>
            {allowSynonyms && values.slice(1).map((synonym, synIdx) => (
                <Box key={synIdx} sx={{ display: 'inline-block', m: 0.5 }}>
                    <Chip
                        label={synonym}
                        onDelete={() => onChange(values.filter((_, totIdx) => totIdx !== synIdx + 1))}
                        color="primary"
                    />
                </Box>
            ))}
            {allowSynonyms && isAddingSynonym && (
                <TextField variant="outlined"
                           spellCheck={spellCheck}
                           autoFocus={true}
                           autoCapitalize="off"
                           autoComplete="off"
                           fullWidth={true}
                           color="secondary"
                           placeholder="Synonym..."
                           size="small"
                           value={currentSynonymInput}
                           inputRef={synonymInputRef}
                           onChange={(e) => setCurrentSynonymInput(e.target.value)}
                           onBlur={() => addSynonym(false)}
                           onKeyDown={(e) => {
                               if (e.key === 'Enter')
                                   addSynonym(true);
                               if (e.key === 'Escape') {
                                   cancelSynonymAddition();
                                   e.stopPropagation();
                               }
                           }}
                           sx={{mt: 1}}
                           InputProps={{
                               style: {fontSize: 14},
                                 endAdornment: (
                                      <>
                                        <IconButton color="primary"
                                                    onMouseDown={(e) => e.preventDefault()}
                                                    onClick={() => addSynonym(true)}>
                                             <CheckIcon />
                                        </IconButton>
                                        <IconButton color="secondary"
                                                    onMouseDown={(e) => e.preventDefault()}
                                                    onClick={cancelSynonymAddition}>
                                             <CloseIcon />
                                        </IconButton>
                                      </>
                                 )
                           }}
                />
            )}
            {!!additionalDisplaySlot && additionalDisplaySlot}
        </Box>
    );

});